import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { UploadService, UploadConfig } from '../services/upload/upload.service';
import { UploadApiService } from '../services/upload/upload-api.service';
import { TemplateService } from '../services/upload/template.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { MastersService } from '../services/masters/masters.service';
import { lastValueFrom } from 'rxjs';
import { getBaseHref, openInNewTab } from '../utils/url.utils';

export interface ValidationError {
  row: number;
  column: string;
  value: string;
  error: string;
}

export interface BulkUploadTab {
  label: string;
  module: string;
  mode: 'create' | 'update';
  formTemplateId?: string | number;
}

@Component({
  selector: 'app-bulk-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    PaginatorModule
  ],
  templateUrl: './bulk-upload.html',
  styleUrls: ['./bulk-upload.scss']
})
export class BulkUploadComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() module: string = '';
  @Input() title: string = '';
  @Input() mode: 'create' | 'update' = 'create';
  @Input() formTemplateId?: string | number;
  @Input() additionalParams?: Record<string, string | number>;
  @Input() backgroundMode: boolean = true; // Background mode by default for async processing
  @Input() tabs: BulkUploadTab[] = []; // Optional tabs for multi-module upload
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() uploadSuccess = new EventEmitter<any>();
  @Output() uploadError = new EventEmitter<any>();
  @Output() backgroundJobCreated = new EventEmitter<any>(); // Emitted when background job is created

  activeTabIndex: number = 0;

  uploadForm!: FormGroup;
  selectedFile: File | null = null;
  fileName: string = '';
  isUploading: boolean = false;
  validationErrors: ValidationError[] = [];
  showValidationErrors: boolean = false;
  errorMessage: string = '';
  successCount: number = 0;
  errorCount: number = 0;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private uploadService: UploadService,
    private uploadApiService: UploadApiService,
    private templateService: TemplateService,
    private toasterService: ToasterService,
    private mastersService: MastersService,
    private router: Router
  ) {}

  ngOnInit() {
    this.uploadForm = this.fb.group({
      file: [null, Validators.required]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && !changes['visible'].currentValue) {
      // Reset component when dialog is closed
      this.resetComponent();
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.fileName = this.selectedFile.name;
      this.uploadForm.patchValue({
        file: this.selectedFile
      });
    }
  }

  async onUpload(): Promise<void> {
    if (this.uploadForm.valid && this.selectedFile && this.activeModule) {
      this.isUploading = true;

      try {
        // Use background mode if enabled
        if (this.backgroundMode) {
          await this.uploadInBackground();
        } else {
          await this.uploadSynchronously();
        }
      } catch (error: any) {
        this.handleErrorResponse(error);
      } finally {
        this.isUploading = false;
      }
    }
  }

  /**
   * Upload file in background mode - creates a job that will be processed asynchronously
   */
  private async uploadInBackground(): Promise<void> {
    if (!this.selectedFile) return;

    // Convert module name to API format (e.g., 'vendor-invoice' -> 'vendor_invoice')
    const moduleKey = this.getModuleKey(this.activeModule);

    const response = await lastValueFrom(
      this.uploadApiService.createUploadJob(
        this.selectedFile,
        moduleKey,
        this.activeMode,
        this.activeFormTemplateId
      )
    );

    if (response?.data) {
      // Success - job created - show toast with link to uploads dashboard
      const baseHref = getBaseHref();
      const uploadsUrl = `${baseHref}/uploads`;
      this.toasterService.successWithLink(
        'Upload Started',
        'Your file is being processed in the background.',
        uploadsUrl,
        'View Progress'
      );
      // Auto-open uploads dashboard in new tab after a short delay
      setTimeout(() => openInNewTab('/uploads'), 1500);
      this.backgroundJobCreated.emit(response.data);
      this.uploadSuccess.emit(response.data);
      this.onClose();
    } else {
      throw new Error(response?.message || 'Failed to create upload job');
    }
  }

  /**
   * Upload file synchronously (original behavior)
   */
  private async uploadSynchronously(): Promise<void> {
    if (!this.selectedFile) return;

    const config: UploadConfig = {
      module: this.activeModule,
      formTemplateId: this.activeFormTemplateId,
      additionalParams: this.additionalParams,
      mode: this.activeMode
    };

    const response = await lastValueFrom(
      this.uploadService.uploadFile(this.selectedFile, config)
    );

    // Check if response has validation errors (even if success is true)
    if (response?.validation_errors && Array.isArray(response.validation_errors) && response.validation_errors.length > 0) {
      // Has validation errors - show them
      this.handleErrorResponse(response);
    } else if (response?.success === true || response?.success === undefined) {
      // True success - no validation errors
      this.toasterService.success('Success', response?.message || 'File uploaded successfully');
      this.uploadSuccess.emit(response);
      this.onClose();
    } else {
      // Handle other error cases
      this.handleErrorResponse(response);
    }
  }

  /**
   * Convert UI module name to API module key format
   */
  private getModuleKey(module: string): string {
    // Map UI module names to API module keys
    const moduleMap: Record<string, string> = {
      'vendor': 'vendor',
      'employee': 'employee',
      'client': 'client',
      'item': 'item',
      'vendor-po': 'vendor_po',
      'vendor-invoice': 'vendor_invoice',
      'vendor-payment': 'vendor_payment',
      'vendor-return': 'vendor_return',
      'vendor-debit-note': 'vendor_debit_note',
      'grn': 'grn',
      'client-order': 'client_order',
      'client-invoice': 'client_invoice',
      'client-payment': 'client_payment',
      'client-return': 'client_return',
      'client-refund': 'client_refund',
      'credit-note': 'client_credit_note',
      'journal-voucher': 'journal_voucher',
      'journal-entry': 'journal_entry',
      'opening-stock': 'opening_stock',
      'account': 'account',
      'inventory': 'opening_stock',
      'bmw-container': 'bmw_container'
    };

    return moduleMap[module] || module.replace(/-/g, '_');
  }

  /**
   * Navigate to uploads dashboard
   */
  goToUploadsDashboard(): void {
    openInNewTab('/uploads');
  }

  handleErrorResponse(error: any): void {
    const errorResponse = error?.error || error;
    
    // Check if it's a validation error response
    if (errorResponse?.validation_errors && Array.isArray(errorResponse.validation_errors)) {
      this.validationErrors = errorResponse.validation_errors;
      this.errorMessage = errorResponse?.message || 'CSV validation failed';
      this.successCount = errorResponse?.success_count || 0;
      this.errorCount = errorResponse?.error_count || this.validationErrors.length;
      this.showValidationErrors = true;
      this.toasterService.error('Validation Error', this.errorMessage);
    } else {
      // Generic error - extract error message from various possible locations
      // Priority: error field (detailed) > message field > generic message
      let errorMsg = errorResponse?.error || errorResponse?.message || error?.error?.error || error?.message || 'Failed to upload file';
      
      // If message is generic but error has more detail, use error
      if (errorMsg === 'Failed to process file' && errorResponse?.error) {
        errorMsg = errorResponse.error;
      }
      
      // Set errorMessage for display in template
      this.errorMessage = errorMsg;
      this.toasterService.error('Error', errorMsg);
      this.uploadError.emit(error);
    }
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetComponent();
  }

  resetComponent(): void {
    this.uploadForm?.reset();
    this.selectedFile = null;
    this.fileName = '';
    this.isUploading = false;
    this.validationErrors = [];
    this.showValidationErrors = false;
    this.errorMessage = '';
    this.successCount = 0;
    this.errorCount = 0;
    this.activeTabIndex = 0;
    // Reset file input
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  closeValidationErrors(): void {
    this.showValidationErrors = false;
    this.validationErrors = [];
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    // Reset file/errors but preserve the tab selection
    this.uploadForm?.reset();
    this.selectedFile = null;
    this.fileName = '';
    this.isUploading = false;
    this.validationErrors = [];
    this.showValidationErrors = false;
    this.errorMessage = '';
    this.successCount = 0;
    this.errorCount = 0;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  get partyOptions(): Array<{ label: string; value: number }> {
    return this.tabs.map((tab, i) => ({ label: tab.label, value: i }));
  }

  onDialogHide(): void {
    this.visibleChange.emit(false);
    this.resetComponent();
  }

  /** Returns the active module based on tabs or direct input */
  get activeModule(): string {
    if (this.tabs.length > 0) {
      return this.tabs[this.activeTabIndex]?.module || this.module;
    }
    return this.module;
  }

  /** Returns the active mode based on tabs or direct input */
  get activeMode(): 'create' | 'update' {
    if (this.tabs.length > 0) {
      return this.tabs[this.activeTabIndex]?.mode || this.mode;
    }
    return this.mode;
  }

  /** Returns the active formTemplateId based on tabs or direct input */
  get activeFormTemplateId(): string | number | undefined {
    if (this.tabs.length > 0) {
      return this.tabs[this.activeTabIndex]?.formTemplateId || this.formTemplateId;
    }
    return this.formTemplateId;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileName = '';
    this.uploadForm.patchValue({
      file: null
    });
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      this.fileName = this.selectedFile.name;
      this.uploadForm.patchValue({
        file: this.selectedFile
      });
    }
  }

  getFileSize(bytes: number | null | undefined): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  openFileDialog(): void {
    this.fileInput?.nativeElement?.click();
  }

  downloadTemplate(): void {
    if (!this.activeModule) {
      this.toasterService.error('Error', 'Module not specified');
      return;
    }

    try {
      // Use mode-specific template if available
      const templateModule = this.activeMode === 'update' ? `${this.activeModule}-update` : this.activeModule;
      this.templateService.downloadTemplate(templateModule);
      // Don't show toast immediately - let the download happen first
      setTimeout(() => {
        this.toasterService.success('Success', 'Template downloaded successfully');
      }, 200);
    } catch (error) {
      this.toasterService.error('Error', 'Failed to download template');
    }
  }

  exportAllMasters(): void {
    this.mastersService.exportAllMasters();
  }

  // Dynamic title based on mode
  get dialogTitle(): string {
    if (this.title) {
      return this.title;
    }
    return this.activeMode === 'update' ? 'Bulk Update' : 'Bulk Upload';
  }

  // Dynamic button text based on mode
  get uploadButtonText(): string {
    return this.activeMode === 'update' ? 'Update' : 'Upload';
  }
}

